import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ACTIVE_THEME, getThemeVars } from '../themes/dashboardThemes';
import { authService } from '../services/api';
import { buildLoginPath, getSafeRedirectPath } from '../utils/redirect';
import { isValidOptionalPhone } from '../utils/phoneValidation';

function RegisterNew() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const theme = getThemeVars(ACTIVE_THEME);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const prefilledEmail = searchParams.get('email') || '';
  const afterLoginPath = useMemo(
    () => getSafeRedirectPath(searchParams.get('after_login_url') || searchParams.get('redirect'), '/'),
    [searchParams]
  );

  const [formData, setFormData] = useState({
    name: '',
    email: prefilledEmail,
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [passwordMatchError, setPasswordMatchError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (name === 'phone') setPhoneError('');
    if (name === 'confirmPassword' || name === 'password') setPasswordMatchError('');
  };

  const handleGoogleRegister = async () => {
    setError('');
    try {
      const response = await authService.getGoogleAuthUrl(afterLoginPath);
      const authUrl = response?.data?.authUrl;
      if (authUrl) {
        window.location.href = authUrl;
      } else {
        setError(t('Error: No se pudo obtener la URL de autenticación'));
      }
    } catch {
      setError(t('Error al iniciar sesión con Google'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setPasswordMatchError('');

    if (formData.password !== formData.confirmPassword) {
      setPasswordMatchError(t('Las contraseñas no coinciden'));
      return;
    }

    // Bloquear plus-aliasing (matute+algo@gmail.com) — el backend igual lo
    // rechaza, pero damos feedback temprano. Espejo de EmailValidator.requireNoPlusAlias
    // del backend.
    const atIdx = (formData.email || '').indexOf('@');
    const localPart = atIdx < 0 ? (formData.email || '') : formData.email.substring(0, atIdx);
    if (localPart.includes('+')) {
      setError(t("El email no puede contener '+' antes de '@'. Usá tu dirección base."));
      return;
    }

    if (!isValidOptionalPhone(formData.phone)) {
      setPhoneError(t('Ingresa un teléfono válido'));
      return;
    }

    setLoading(true);

    try {
      await authService.register(
        formData.email,
        formData.name,
        formData.password,
        formData.phone,
        afterLoginPath !== '/' ? afterLoginPath : null
      );
      setSuccess(true);
      setTimeout(() => {
        navigate(buildLoginPath(afterLoginPath));
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.message || t('Error al registrar usuario'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="dashboard-new font-body min-h-[100dvh] flex items-center justify-center px-4 py-8"
      style={{ ...theme, backgroundColor: 'var(--chrome-bg)', color: 'var(--chrome-text)' }}
    >
      <div className="w-full max-w-md">
        <div className="bg-surface rounded-xl shadow-sm border border-outline p-8">
          <h1 className="text-3xl font-bold text-center text-primary">{t('Vatuco')}</h1>
          <h2 className="text-lg font-medium text-center text-on-surface-variant mt-2">{t('Crear Cuenta')}</h2>

          {error && (
            <div id="reg-error" role="alert" className="mt-4 flex items-center gap-2 p-3 rounded-lg bg-error-container text-on-error-container text-sm">
              <span className="material-symbols-outlined text-[18px]">error</span>
              {error}
            </div>
          )}

          {success && (
            <div className="mt-4 flex items-center gap-2 p-3 rounded-lg bg-primary-container text-on-primary-container text-sm">
              <span className="material-symbols-outlined text-[18px]">check_circle</span>
              {t('Registro exitoso! Por favor verifica tu correo electrónico. Redirigiendo...')}
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            {/* Name */}
            <div className="relative">
              <input
                type="text"
                id="reg-name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                autoComplete="name"
                autoFocus
                placeholder={t('Nombre Completo')}
                className="peer w-full border-2 border-outline-variant/30 rounded-lg px-3.5 pt-5 pb-2 text-base text-on-surface bg-white placeholder-transparent transition-colors"
              />
              <label htmlFor="reg-name" className="absolute left-3 -top-2.5 bg-white px-1 text-sm text-on-surface-variant peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-base peer-placeholder-shown:text-on-surface-variant/60 peer-focus:-top-2.5 peer-focus:text-sm peer-focus:text-primary transition-all">
                {t('Nombre Completo')} *
              </label>
            </div>

            {/* Email */}
            <div className="relative">
              <input
                type="email"
                id="reg-email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                autoComplete="email"
                placeholder={t('Correo Electrónico')}
                className="peer w-full border-2 border-outline-variant/30 rounded-lg px-3.5 pt-5 pb-2 text-base text-on-surface bg-white placeholder-transparent transition-colors"
              />
              <label htmlFor="reg-email" className="absolute left-3 -top-2.5 bg-white px-1 text-sm text-on-surface-variant peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-base peer-placeholder-shown:text-on-surface-variant/60 peer-focus:-top-2.5 peer-focus:text-sm peer-focus:text-primary transition-all">
                {t('Correo Electrónico')} *
              </label>
            </div>

            {/* Phone */}
            <div className="relative">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 material-symbols-outlined text-on-surface-variant text-[20px]">phone</span>
              <input
                type="tel"
                id="reg-phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                autoComplete="tel"
                aria-invalid={!!phoneError}
                aria-describedby={phoneError ? 'reg-phone-error' : undefined}
                placeholder={t('Teléfono (opcional)')}
                className={`peer w-full border-2 rounded-lg pl-10 pr-3.5 pt-5 pb-2 text-base text-on-surface bg-white placeholder-transparent transition-colors ${phoneError ? 'border-error' : 'border-outline-variant/30'}`}
              />
              <label htmlFor="reg-phone" className="absolute left-10 -top-2.5 bg-white px-1 text-sm text-on-surface-variant peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-base peer-placeholder-shown:text-on-surface-variant/60 peer-focus:-top-2.5 peer-focus:text-sm peer-focus:text-primary transition-all">
                {t('Teléfono (opcional)')}
              </label>
              {phoneError && <p id="reg-phone-error" className="text-xs text-error mt-1">{phoneError}</p>}
            </div>

            {/* Password */}
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                id="reg-password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                autoComplete="new-password"
                aria-invalid={!!error}
                aria-describedby={error ? 'reg-error' : undefined}
                placeholder={t('Contraseña')}
                className="peer w-full border-2 border-outline-variant/30 rounded-lg px-3.5 pt-5 pb-2 pr-12 text-base text-on-surface bg-white placeholder-transparent transition-colors"
              />
              <label htmlFor="reg-password" className="absolute left-3 -top-2.5 bg-white px-1 text-sm text-on-surface-variant peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-base peer-placeholder-shown:text-on-surface-variant/60 peer-focus:-top-2.5 peer-focus:text-sm peer-focus:text-primary transition-all">
                {t('Contraseña')} *
              </label>
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-1 top-1/2 -translate-y-1/2 inline-flex items-center justify-center min-w-[44px] min-h-[44px] text-on-surface-variant hover:text-on-surface transition-colors bg-transparent"
              >
                <span className="material-symbols-outlined text-[20px]">
                  {showPassword ? 'visibility_off' : 'visibility'}
                </span>
              </button>
            </div>

            {/* Confirm Password */}
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="reg-confirm"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                aria-invalid={!!passwordMatchError}
                aria-describedby={passwordMatchError ? 'reg-confirm-error' : undefined}
                placeholder={t('Confirmar Contraseña')}
                className={`peer w-full border-2 rounded-lg px-3.5 pt-5 pb-2 pr-12 text-base text-on-surface bg-white placeholder-transparent transition-colors ${passwordMatchError ? 'border-error' : 'border-outline-variant/30'}`}
              />
              <label htmlFor="reg-confirm" className="absolute left-3 -top-2.5 bg-white px-1 text-sm text-on-surface-variant peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-base peer-placeholder-shown:text-on-surface-variant/60 peer-focus:-top-2.5 peer-focus:text-sm peer-focus:text-primary transition-all">
                {t('Confirmar Contraseña')} *
              </label>
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-1 top-1/2 -translate-y-1/2 inline-flex items-center justify-center min-w-[44px] min-h-[44px] text-on-surface-variant hover:text-on-surface transition-colors bg-transparent"
              >
                <span className="material-symbols-outlined text-[20px]">
                  {showConfirmPassword ? 'visibility_off' : 'visibility'}
                </span>
              </button>
              {passwordMatchError && <p id="reg-confirm-error" className="text-xs text-error mt-1">{passwordMatchError}</p>}
            </div>

            <button
              type="submit"
              disabled={loading || success}
              className="w-full py-3 rounded-lg bg-primary text-on-primary font-medium text-base transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {loading ? t('Registrando...') : t('Registrarse')}
            </button>
          </form>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-outline-variant/20" />
            <span className="text-sm text-on-surface-variant">{t('O')}</span>
            <div className="flex-1 h-px bg-outline-variant/20" />
          </div>

          <button
            onClick={handleGoogleRegister}
            disabled={loading || success}
            className="w-full flex items-center justify-center gap-3 py-3 rounded-lg border-2 border-outline-variant/30 text-on-surface font-medium text-base hover:bg-surface-container-low transition-colors bg-transparent disabled:opacity-50"
          >
            <svg width="20" height="20" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
            {t('Registrarse con Google')}
          </button>

          <p className="text-center text-sm text-on-surface-variant mt-6">
            {t('¿Ya tienes cuenta?')}{' '}
            <Link to={buildLoginPath(afterLoginPath)} className="text-primary font-medium hover:underline">
              {t('Iniciar Sesión')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default RegisterNew;
